import { useState, useEffect, useCallback, useRef } from 'react';
import { useDebouncedCallback } from '@mantine/hooks';
import { userPreferencesApi } from '../services/api/userPreferences';

/**
 * Column configuration for a table
 */
export interface ColumnConfig {
  id: string;
  label: string;
  visible: boolean;
  sortable: boolean;
  isCustomField?: boolean;
  /** Whether this column can be used for Group By. Defaults to true if omitted. */
  groupable?: boolean;
  /** Backend export column key. If omitted, defaults to `id`. Set to `null` for non-exportable columns. */
  exportKey?: string | null;
  /** MDF-41: the column holds numbers, so it shows an average. Sets the number format. */
  numeric?: NumericFormat;
  /** MDF-209: the code default when `visible` comes from a saved layout */
  defaultVisible?: boolean;
}

/** MDF-41: how a numeric column formats its values and its average. */
export type NumericFormat = 'percent' | 'decimal';

/**
 * Custom field definition from API
 */
export interface CustomFieldDefinition {
  id: string;
  field_name: string;
  field_label: string;
  field_type: string;
  sort_order: number;
}

/**
 * Sort configuration
 */
export interface SortConfig {
  key: string;
  order: 'ASC' | 'DESC';
}

/**
 * Saved preferences shape
 */
interface ColumnPreferences {
  columns: ColumnConfig[];
  pageSize: number;
  sortConfig: SortConfig | null;
}

/**
 * Options for useColumnConfig hook
 */
export interface UseColumnConfigOptions {
  /** Unique key for storing preferences (e.g., 'participants_settings_cohortId') */
  preferenceKey: string;
  /** Default column configuration */
  defaultColumns: ColumnConfig[];
  /** Default page size */
  defaultPageSize?: number;
  /** Custom field definitions from API (optional) */
  customFieldDefinitions?: CustomFieldDefinition[];
  /** Callback to delete a custom field */
  onDeleteCustomField?: (fieldId: string) => Promise<void>;
}

/**
 * Return type for useColumnConfig hook
 */
export interface UseColumnConfigReturn {
  /** Current column configuration */
  columns: ColumnConfig[];
  /** Update columns */
  setColumns: (columns: ColumnConfig[]) => void;
  /** Current sort configuration */
  sortConfig: SortConfig | null;
  /** Handle sort toggle */
  handleSort: (key: string) => void;
  /** Clear column header sort (used when dropdown sort changes) */
  clearSort: () => void;
  /** Current page */
  page: number;
  /** Set current page */
  setPage: (page: number) => void;
  /** Current page size */
  pageSize: number;
  /** Set page size (resets to page 1) */
  setPageSize: (size: number) => void;
  /** Whether preferences have been loaded */
  isLoaded: boolean;
  /** Count of visible columns */
  visibleColumnCount: number;
  /** Handle deleting a custom column */
  handleDeleteCustomColumn: (columnId: string) => Promise<void>;
}

/**
 * Hook for managing configurable table columns with persistence
 */
export function useColumnConfig({
  preferenceKey,
  defaultColumns,
  defaultPageSize = 25,
  customFieldDefinitions = [],
  onDeleteCustomField,
}: UseColumnConfigOptions): UseColumnConfigReturn {
  const [columns, setColumnsState] = useState<ColumnConfig[]>(defaultColumns);
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSizeState] = useState(defaultPageSize);
  const [isLoaded, setIsLoaded] = useState(false);

  const preferencesLoadedRef = useRef(false);

  // Load preferences on mount
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const saved = await userPreferencesApi.get<ColumnPreferences>(preferenceKey);
        if (saved) {
          // Merge saved columns with defaults (preserves new default columns)
          if (saved.columns && Array.isArray(saved.columns)) {
            const mergedColumns = defaultColumns.map((defaultCol) => {
              const savedCol = saved.columns.find((s) => s.id === defaultCol.id);
              // MED-383: sortable and label (MDF-41: and numeric) are code-level decisions, always use default
              return savedCol
                ? { ...defaultCol, ...savedCol, label: defaultCol.label, sortable: defaultCol.sortable, numeric: defaultCol.numeric }
                : defaultCol;
            });
            // Keep custom columns that are in saved preferences
            const customColumns = saved.columns.filter(
              (s) => s.isCustomField && !defaultColumns.find((d) => d.id === s.id)
            );
            setColumnsState([...mergedColumns, ...customColumns]);
          }
          if (saved.pageSize) {
            setPageSizeState(saved.pageSize);
          }
          if (saved.sortConfig) {
            setSortConfig(saved.sortConfig);
          }
        }
      } catch {
        // Preference doesn't exist yet, use defaults
      } finally {
        preferencesLoadedRef.current = true;
        setIsLoaded(true);
      }
    };
    loadPreferences();
  }, [preferenceKey, defaultColumns]);

  // Merge custom fields into column config after preferences are loaded
  // Also ensure labels stay in sync with code-level defaults
  useEffect(() => {
    if (!isLoaded) return;

    setColumnsState((currentConfig) => {
      let hasChanges = false;

      // Sync labels from defaults for built-in columns
      const defaultLabelMap = new Map(defaultColumns.map((d) => [d.id, d.label]));
      const synced = currentConfig.map((col) => {
        if (!col.isCustomField && defaultLabelMap.has(col.id)) {
          const defaultLabel = defaultLabelMap.get(col.id)!;
          if (col.label !== defaultLabel) {
            hasChanges = true;
            return { ...col, label: defaultLabel };
          }
        }
        return col;
      });

      // Get existing custom field ids in current config
      const existingCustomFieldIds = new Set(
        synced.filter((c) => c.isCustomField).map((c) => c.id)
      );

      // Get new custom fields that aren't already in the config
      const newCustomFields = customFieldDefinitions
        .filter((cf) => !existingCustomFieldIds.has(cf.field_name))
        .map((cf) => ({
          id: cf.field_name,
          label: cf.field_label,
          visible: false,
          sortable: true,
          isCustomField: true,
        }));

      if (newCustomFields.length > 0) hasChanges = true;

      // Remove custom fields that no longer exist in definitions
      const validCustomFieldNames = new Set(customFieldDefinitions.map((cf) => cf.field_name));
      const filteredConfig = synced.filter(
        (c) => !c.isCustomField || validCustomFieldNames.has(c.id)
      );

      if (filteredConfig.length !== synced.length) hasChanges = true;

      // Only update if there are actual changes
      if (!hasChanges) {
        return currentConfig;
      }

      return [...filteredConfig, ...newCustomFields];
    });
  }, [isLoaded, customFieldDefinitions, defaultColumns]);

  // Save preferences when they change (debounced)
  const savePreferences = useDebouncedCallback(async (prefs: ColumnPreferences) => {
    if (!preferencesLoadedRef.current) return;
    try {
      await userPreferencesApi.set(preferenceKey, prefs);
    } catch (error) {
      console.error('Failed to save preferences:', error);
    }
  }, 500);

  // Trigger save when any preference changes
  useEffect(() => {
    if (preferencesLoadedRef.current) {
      savePreferences({ columns, pageSize, sortConfig });
    }
  }, [columns, pageSize, sortConfig, savePreferences]);

  // Set columns wrapper
  const setColumns = useCallback((newColumns: ColumnConfig[]) => {
    setColumnsState(newColumns);
  }, []);

  // Handle sort toggle
  const handleSort = useCallback((key: string) => {
    setSortConfig((current) => {
      if (current?.key === key) {
        if (current.order === 'ASC') {
          return { key, order: 'DESC' };
        }
        return null;
      }
      return { key, order: 'ASC' };
    });
    setPage(1); // Reset to first page on sort change
  }, []);

  // Clear column header sort (allows dropdown sort to take effect)
  const clearSort = useCallback(() => {
    setSortConfig(null);
    setPage(1);
  }, []);

  // Set page size (resets to page 1)
  const setPageSize = useCallback((size: number) => {
    setPageSizeState(size);
    setPage(1);
  }, []);

  // Handle deleting a custom column
  const handleDeleteCustomColumn = useCallback(async (columnId: string) => {
    const fieldDef = customFieldDefinitions.find((cf) => cf.field_name === columnId);
    if (!fieldDef) {
      throw new Error('Custom field definition not found');
    }

    if (onDeleteCustomField) {
      await onDeleteCustomField(fieldDef.id);
    }

    // Remove from columns
    setColumnsState((current) => current.filter((col) => col.id !== columnId));
  }, [customFieldDefinitions, onDeleteCustomField]);

  // Count visible columns
  const visibleColumnCount = columns.filter((c) => c.visible).length + 1; // +1 for actions column

  return {
    columns,
    setColumns,
    sortConfig,
    handleSort,
    clearSort,
    page,
    setPage,
    pageSize,
    setPageSize,
    isLoaded,
    visibleColumnCount,
    handleDeleteCustomColumn,
  };
}

export default useColumnConfig;
